import styled from 'styled-components'

import { ResponsiveImage } from '@syconium/little-miss-figgy'

import { ProductCatalogEmbroideryPlacement } from '../../../../../__generated__/graphql/catalog/graphql'
import { useEmbroidery } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { getEmbroideryPlacementKey } from '../../../../../containers/EmbroideryContainer/utils/getEmbroideryPlacementKey'
import { getFlatlayImageOffset } from '../../../../../containers/EmbroideryContainer/utils/getFlatlayImageOffset'
import { EMBROIDERY_IMAGE_FILTER_ID } from '../../../Modal/primitives/EmbroideryThreadFilter'
import { usePreviewFlatlayImage } from '../../EmbroideryModalPreview/PreviewFlatlayImageContext'

const LOGO_Y_OFFSET_BY_PREVIEW = {
  full: 1,
  zoom: -1,
} as const
const LOGO_TEXT_OVERLAP_Y_OFFSET = -8

export const LogoPreviewItem = <
  T extends {
    logoPlacement?: ProductCatalogEmbroideryPlacement | undefined
    logoImageSource?: string
    logoPreview?: string
    logoColor?: 'BLACK_WHITE' | 'COLOR'
    logoFile?: {
      preview?: string
      digitizationSetting?: 'BLACK_WHITE' | 'COLOR'
    }
  },
>({
  values,
}: {
  values: T
}) => {
  const {
    productEmbroideryConfig,
    baseCatalogLogoPlacements,
    productFlatlayImage,
    embroiderySelections,
    submittedEmbroiderySelections,
    selectedFormKind,
    currentPlacement,
    isB2bEmbroidery,
    isTeamStoreEmbroidery,
  } = useEmbroidery()
  const previewFlatlayImage = usePreviewFlatlayImage()
  const activeFlatlayImage = previewFlatlayImage ?? productFlatlayImage

  // Legacy portals (B2B but not team store) do not define a logo placement because Ops picks it
  // internally, so any preview would just guess the left chest. We suppress the overlay here.
  const isPortalEmbroidery = isB2bEmbroidery && !isTeamStoreEmbroidery

  // Fallback for cases where B2B form state has no explicit placement.
  const defaultLogoPlacement =
    productEmbroideryConfig?.options.logo?.placements.find(
      placement => placement.location === 'CHEST' && placement.position === 'LEFT'
    ) ?? productEmbroideryConfig?.options.logo?.placements[0]

  const logoPlacement = values.logoPlacement ?? defaultLogoPlacement
  const logoImageSrc = values.logoImageSource ?? values.logoPreview ?? values.logoFile?.preview
  // Render previews only for base-catalog logo placements.
  const baseCatalogLogoPlacementKeys = baseCatalogLogoPlacements
    ? new Set(baseCatalogLogoPlacements.map(getEmbroideryPlacementKey))
    : null

  const backgroundImageOffsets = getFlatlayImageOffset({
    flatlayImage: activeFlatlayImage,
    placement: logoPlacement,
  })

  const textPlacement =
    submittedEmbroiderySelections.TEXT?.textPlacement ?? embroiderySelections.TEXT?.textPlacement
  const hasSavedTextPlacementOverlap =
    textPlacement?.location === logoPlacement?.location &&
    textPlacement?.position === logoPlacement?.position
  const hasActiveTextPlacementOverlap =
    selectedFormKind === 'TEXT' &&
    currentPlacement?.location === logoPlacement?.location &&
    currentPlacement?.position === logoPlacement?.position
  const hasTextPlacementOverlap = hasSavedTextPlacementOverlap || hasActiveTextPlacementOverlap

  const xOffset: string | undefined = backgroundImageOffsets?.x
    ? `${backgroundImageOffsets.x * 100}%`
    : '55%'
  const logoPreviewMode = activeFlatlayImage?.location ? 'zoom' : 'full'
  const logoPreviewYOffset = hasTextPlacementOverlap
    ? LOGO_TEXT_OVERLAP_Y_OFFSET + LOGO_Y_OFFSET_BY_PREVIEW[logoPreviewMode]
    : 0
  const yOffset = `calc(${
    backgroundImageOffsets?.y ? backgroundImageOffsets.y * 100 : 33
  }% + ${logoPreviewYOffset}%)`
  const rotation = backgroundImageOffsets?.rotation ?? 0
  const scale = backgroundImageOffsets?.scale ?? 1

  const isBlackAndWhite =
    (values.logoColor ?? values.logoFile?.digitizationSetting) === 'BLACK_WHITE'

  if (isPortalEmbroidery) return null

  if (
    baseCatalogLogoPlacementKeys &&
    logoPlacement &&
    !baseCatalogLogoPlacementKeys.has(getEmbroideryPlacementKey(logoPlacement))
  )
    return null

  if (!logoImageSrc || !logoPlacement || !backgroundImageOffsets) return null

  return (
    <Container
      style={{
        left: xOffset,
        top: yOffset,
        transform: `translate(-50%, -50%) rotateZ(${rotation}deg) scale(${scale})`,
      }}
    >
      <LogoImageWrapper>
        <LogoImage
          $grayscale={isBlackAndWhite}
          src={logoImageSrc}
          alt=''
          loading='default'
          widths={{ unit: 'px', sm: 112, md: 112 }}
          aspectRatios={{ sm: null, md: null }}
          transparentBackground
        />
      </LogoImageWrapper>
    </Container>
  )
}

const Container = styled.div`
  position: absolute;
  z-index: ${o => o.theme.zIndex.foregroundContent};
  border-radius: ${o => o.theme.borderRadius.sm};
  padding: ${o => o.theme.spacing(1)};
  isolation: isolate;
`

const LogoImageWrapper = styled.div`
  display: inline-block;
  mix-blend-mode: multiply;
`

const LogoImage = styled(ResponsiveImage)<{ $grayscale: boolean }>`
  display: block;
  width: clamp(64px, 18cqw, 112px);
  height: clamp(48px, 13.5cqw, 84px);
  object-fit: contain;
  filter: url(#${EMBROIDERY_IMAGE_FILTER_ID}) ${o => (o.$grayscale ? 'grayscale(1)' : '')};
`
